import { createCachedHook } from './base.store';
import { shopApi } from '../manager/shop';
import { mainBodyApi, MainBody } from '../manager/main-body';
import { filterCategoryApi } from '../manager/filter-category';
import { roleApi } from '../manager/role';
import { departmentApi } from '../manager/department';
import { staffApi } from '../manager/staff';
import { Shop } from '../../types/entity/shop';
import { RoleItem } from '../../types/entity/role';
import { DepartmentItem } from '../../types/entity/department';
import type { StaffItem } from '../../types/entity/staff';

/**
 * React Hook: 获取店铺 Map 与店铺数组
 * 仅返回原始数据，不派生 options
 */
export const useShopMap = createCachedHook<Shop, 'shop'>({
  cacheKey: 'shop:map',
  ttlMsDefault: 30 * 60 * 1000,
  fetchItems: async () => {
    const resp = await shopApi.getAll();
    return (resp?.data?.items || []) as Shop[];
  },
  keySelector: (s: Shop) => Number((s as any).code ?? (s as any).id),
  subjectKey: 'shop',
});

/**
 * React Hook: 获取主体 Map 与主体数组
 * 仅返回原始数据，不派生 options
 */
export const useMainBodyMap = createCachedHook<MainBody, 'mainBody'>({
  cacheKey: 'main-body:map',
  ttlMsDefault: 30 * 60 * 1000,
  fetchItems: async () => {
    const resp = await mainBodyApi.getAll();
    return (resp?.data?.items || []) as MainBody[];
  },
  keySelector: (b: MainBody) => Number((b as any).id),
  subjectKey: 'mainBody',
});

/**
 * React Hook: 获取角色 Map 与角色数组
 */
export const useRoleMap = createCachedHook<RoleItem, 'role'>({
  cacheKey: 'role:map',
  ttlMsDefault: 10 * 60 * 1000,
  fetchItems: async () => {
    const resp = await roleApi.getAll();
    return (resp?.data?.items || []) as RoleItem[];
  },
  keySelector: (r: RoleItem) => r.id,
  subjectKey: 'role',
});

/**
 * React Hook: 获取部门 Map 与部门数组
 */
export const useDepartmentMap = createCachedHook<DepartmentItem, 'department'>({
  cacheKey: 'department:map',
  ttlMsDefault: 30 * 60 * 1000,
  fetchItems: async () => {
    const resp = await departmentApi.getAll();
    return (resp?.data?.items || []) as DepartmentItem[];
  },
  keySelector: (d: DepartmentItem) => d.id,
  subjectKey: 'department',
});

/**
 * React Hook: 获取员工 Map 与员工数组
 */
export const useStaffMap = createCachedHook<StaffItem, 'staff'>({
  cacheKey: 'staff:map',
  ttlMsDefault: 10 * 60 * 1000,
  fetchItems: async () => {
    const resp = await staffApi.list({ page: 1, pageSize: 1000 } as any);
    return (resp?.data?.items || []) as StaffItem[];
  },
  keySelector: (s: StaffItem) => s.id,
  subjectKey: 'staff',
});

/**
 * 获取 AppType 选项（来源：filter-categories.type=appType）
 */
export const useAppTypes = createCachedHook<{ key: string; name: string }, 'appType'>({
  cacheKey: 'filter-category:appType',
  ttlMsDefault: 30 * 60 * 1000,
  fetchItems: async () => {
    const resp = await filterCategoryApi.list({ page: 1, pageSize: 200, filter: { type: 'appType' }, sort: { name: 1 } } as any);
    return ((resp?.data?.items || []) as any[]).map((i: any) => ({ key: i.key, name: i.name ?? i.key }));
  },
  subjectKey: 'appType',
});

/**
 * 获取 Auditor 选项（来源：filter-categories.type=auditor）
 */
export const useAuditors = createCachedHook<{ key: string; name: string }, 'auditor'>({
  cacheKey: 'filter-category:auditor',
  ttlMsDefault: 30 * 60 * 1000,
  fetchItems: async () => {
    const resp = await filterCategoryApi.list({ page: 1, pageSize: 200, filter: { type: 'auditor' }, sort: { name: 1 } } as any);
    return ((resp?.data?.items || []) as any[]).map((i: any) => ({ key: i.key, name: i.name ?? i.key }));
  },
  subjectKey: 'auditor',
});

/**
 * 获取 Brand 选项（来源：filter-categories.type=brand）
 */
export const useBrands = createCachedHook<{ key: string; name: string }, 'brand'>({
  cacheKey: 'filter-category:brand',
  ttlMsDefault: 30 * 60 * 1000,
  fetchItems: async () => {
    // Assuming type 'brand' exists
    const resp = await filterCategoryApi.list({ page: 1, pageSize: 200, filter: { type: 'brand' }, sort: [{ field: 'name', order: 'asc' }] } as any);
    return ((resp?.data?.items || []) as any[]).map((i: any) => ({ key: i.key, name: i.name ?? i.key }));
  },
  subjectKey: 'brand',
});

/**
 * 获取 Model/Sku 选项（来源：filter-categories.type=sku）
 * Note: If SKU depends on Brand, this global list might be large.
 * Ideally, we should fetch on demand or have a way to filter. 
 * But following the pattern, we fetch all.
 */
export const useSkus = createCachedHook<{ key: string; name: string; parentKey?: string }, 'sku'>({
  cacheKey: 'filter-category:sku',
  ttlMsDefault: 30 * 60 * 1000,
  fetchItems: async () => {
    // Assuming type 'sku' exists
    const resp = await filterCategoryApi.list({ page: 1, pageSize: 1000, filter: { type: 'sku' }, sort: [{ field: 'name', order: 'asc' }] } as any);
    return ((resp?.data?.items || []) as any[]).map((i: any) => ({ 
      key: i.key, 
      name: i.name ?? i.key,
      parentKey: i.parentKey // Assuming relationship exists in data
    }));
  },
  subjectKey: 'sku',
});
